import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useGrupoEmpresa } from '../../contexts/GrupoEmpresaContext';
import { getGrupoByEmpresaId, getConvitesPendentes } from '../../mocks/admin/gruposEmpresas';
import LanguageSelector from './LanguageSelector';

export default function Header() {
  const { t } = useTranslation();
  const { empresaSelecionada, empresas, selecionarEmpresa, empresaPertenceAGrupo } = useEmpresa();
  const { isAuthenticated, session, logout, empresaNome } = useAdminAuth();
  const { convitesPendentes, aceitarConvite, rejeitarConvite, carregarDados: carregarGrupo } = useGrupoEmpresa();
  
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSaldoCombo, setShowSaldoCombo] = useState(false);
  const [hideSaldoValue, setHideSaldoValue] = useState(false);
  const [showEmpresaSelector, setShowEmpresaSelector] = useState(false);

  // Estado para os saldos dinâmicos por empresa
  const [saldos, setSaldos] = useState({
    contaDigital: 'R$ 2.450,00',
    pontosIncentivo: '15.750',
    valorContaDigital: 2450.00,
    valorPontos: 15750.00
  });

  // Dados de saldos por empresa
  const saldosPorEmpresa = {
    1: { // TechCorp Soluções Ltda
      contaDigital: 'R$ 2.450,00',
      pontosIncentivo: '15.750',
      valorContaDigital: 2450.00,
      valorPontos: 15750.00
    },
    2: { // Inovação Digital S.A.
      contaDigital: 'R$ 1.890,00',
      pontosIncentivo: '12.300',
      valorContaDigital: 1890.00,
      valorPontos: 12300.00
    },
    3: { // Consultoria Tech Brasil
      contaDigital: 'R$ 3.120,00',
      pontosIncentivo: '18.950',
      valorContaDigital: 3120.00,
      valorPontos: 18950.00
    },
    4: { // Sistemas Integrados Ltda
      contaDigital: 'R$ 1.650,00',
      pontosIncentivo: '9.200',
      valorContaDigital: 1650.00,
      valorPontos: 9200.00
    }
  };

  // Função para carregar saldos da empresa
  const carregarSaldosEmpresa = (empresaId: number) => {
    const saldosEmpresa = saldosPorEmpresa[empresaId as keyof typeof saldosPorEmpresa];
    if (saldosEmpresa) {
      console.log('💰 Carregando saldos da empresa:', empresaId, saldosEmpresa);
      setSaldos(saldosEmpresa);
    }
  };

  // Escutar mudanças de empresa
  useEffect(() => {
    const handleEmpresaChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { empresaId } = customEvent.detail;
      console.log('🔄 Header detectou mudança de empresa para:', empresaId);
      carregarSaldosEmpresa(empresaId);
    };

    window.addEventListener('empresaAlterada', handleEmpresaChange);
    
    // Carregar saldos iniciais
    carregarSaldosEmpresa(empresaSelecionada.id);

    return () => {
      window.removeEventListener('empresaAlterada', handleEmpresaChange);
    };
  }, [empresaSelecionada.id]);

  // Carregar dados do grupo quando empresa muda
  useEffect(() => {
    if (empresaSelecionada.empresaBackofficeId) {
      carregarGrupo(empresaSelecionada.empresaBackofficeId);
    }
  }, [empresaSelecionada.empresaBackofficeId, carregarGrupo]);

  // Grupo da empresa atual
  const grupoAtual = empresaSelecionada.empresaBackofficeId
    ? getGrupoByEmpresaId(empresaSelecionada.empresaBackofficeId)
    : null;

  // Adicionar convites de grupo como notificações
  const [notifications, setNotifications] = useState([
    // Premiações Recebidas
    { 
      id: 1, 
      message: '🎉 Parabéns! Você ganhou um iPhone 15 Pro na Roleta da Sorte!', 
      time: '5 min atrás', 
      unread: true,
      type: 'reward',
      icon: 'ri-gift-line',
      color: 'purple',
      category: 'premiacao'
    },
    { 
      id: 2, 
      message: '🏆 Premiação desbloqueada: Vale-Compras R$ 500 por atingir meta mensal', 
      time: '1 hora atrás', 
      unread: true,
      type: 'reward',
      icon: 'ri-trophy-line',
      color: 'orange',
      category: 'premiacao'
    },
    { 
      id: 3, 
      message: '💎 Você recebeu 250 pontos de bônus por completar 5 missões seguidas!', 
      time: '3 horas atrás', 
      unread: true,
      type: 'points',
      icon: 'ri-coin-line',
      color: 'emerald',
      category: 'premiacao'
    },
    { 
      id: 4, 
      message: '🎁 Prêmio especial: AirPods Pro disponível para resgate', 
      time: '5 horas atrás', 
      unread: false,
      type: 'reward',
      icon: 'ri-gift-2-line',
      color: 'purple',
      category: 'premiacao'
    },

    // Missões Concluídas
    { 
      id: 5, 
      message: '✅ Missão "Meta Diária de Vendas" concluída! +120 XP e 40 pontos', 
      time: '2 horas atrás', 
      unread: true,
      type: 'achievement',
      icon: 'ri-checkbox-circle-line',
      color: 'emerald',
      category: 'missao'
    },
    { 
      id: 6, 
      message: '🎯 Missão "Conquistador Semanal" concluída! +300 XP e 150 pontos', 
      time: '1 dia atrás', 
      unread: false,
      type: 'achievement',
      icon: 'ri-target-line',
      color: 'blue',
      category: 'missao'
    },
    { 
      id: 7, 
      message: '⭐ Missão "Atendimentos de Qualidade" concluída! +100 XP', 
      time: '1 dia atrás', 
      unread: false,
      type: 'achievement',
      icon: 'ri-star-line',
      color: 'orange',
      category: 'missao'
    },
    { 
      id: 8, 
      message: '🏅 Missão especial "Mestre do Mês" concluída! +800 XP e 500 pontos', 
      time: '2 dias atrás', 
      unread: false,
      type: 'achievement',
      icon: 'ri-medal-line',
      color: 'purple',
      category: 'missao'
    },

    // Alertas do Sistema
    { 
      id: 9, 
      message: '⚠️ Atenção: Sua missão "Meta Diária" expira em 2 horas!', 
      time: '30 min atrás', 
      unread: true,
      type: 'alert',
      icon: 'ri-alarm-warning-line',
      color: 'red',
      category: 'alerta'
    },
    { 
      id: 10, 
      message: '⏰ Lembrete: Você tem 3 missões pendentes para hoje', 
      time: '4 horas atrás', 
      unread: false,
      type: 'alert',
      icon: 'ri-time-line',
      color: 'orange',
      category: 'alerta'
    },
    { 
      id: 11, 
      message: '📊 Você está a apenas 200 XP do próximo nível!', 
      time: '6 horas atrás', 
      unread: false,
      type: 'alert',
      icon: 'ri-line-chart-line',
      color: 'blue',
      category: 'alerta'
    },
    { 
      id: 12, 
      message: '🎮 Novo jogo disponível: "Simulador de Atendimento" - Jogue agora!', 
      time: '8 horas atrás', 
      unread: false,
      type: 'alert',
      icon: 'ri-gamepad-line',
      color: 'purple',
      category: 'alerta'
    },
    { 
      id: 13, 
      message: '💰 Seus pontos expiram em 15 dias. Resgate suas recompensas!', 
      time: '1 dia atrás', 
      unread: false,
      type: 'alert',
      icon: 'ri-error-warning-line',
      color: 'red',
      category: 'alerta'
    },

    // Mensagens da Patrocinadora
    { 
      id: 14, 
      message: '📢 TechCorp: Parabéns pelo excelente desempenho neste mês! Continue assim!', 
      time: '2 horas atrás', 
      unread: true,
      type: 'message',
      icon: 'ri-message-3-line',
      color: 'blue',
      category: 'mensagem'
    },
    { 
      id: 15, 
      message: '🎊 TechCorp: Nova campanha "Verão 2024" iniciada! Confira os detalhes', 
      time: '1 dia atrás', 
      unread: false,
      type: 'campaign',
      icon: 'ri-megaphone-line',
      color: 'purple',
      category: 'mensagem'
    },
    { 
      id: 16, 
      message: '💼 TechCorp: Reunião de alinhamento de metas agendada para sexta-feira', 
      time: '1 dia atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-calendar-event-line',
      color: 'blue',
      category: 'mensagem'
    },
    { 
      id: 17, 
      message: '🌟 TechCorp: Você foi destaque da semana! Seu esforço foi reconhecido', 
      time: '2 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-vip-crown-line',
      color: 'orange',
      category: 'mensagem'
    },
    { 
      id: 18, 
      message: '📈 TechCorp: Meta trimestral atualizada. Novos bônus disponíveis!', 
      time: '3 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-bar-chart-box-line',
      color: 'emerald',
      category: 'mensagem'
    },
    { 
      id: 19, 
      message: '🎓 TechCorp: Novo treinamento disponível sobre técnicas de vendas', 
      time: '3 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-book-open-line',
      color: 'blue',
      category: 'mensagem'
    },
    { 
      id: 20, 
      message: '🎉 TechCorp: Evento de premiação será realizado no próximo mês!', 
      time: '4 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-calendar-check-line',
      color: 'purple',
      category: 'mensagem'
    },
    { 
      id: 21, 
      message: '💡 TechCorp: Dica do dia - Use os jogos para ganhar XP extra rapidamente', 
      time: '5 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-lightbulb-line',
      color: 'orange',
      category: 'mensagem'
    },
    { 
      id: 22, 
      message: '🏆 TechCorp: Ranking atualizado! Você subiu 2 posições esta semana', 
      time: '5 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-trophy-line',
      color: 'emerald',
      category: 'mensagem'
    },
    { 
      id: 23, 
      message: '🎁 TechCorp: Novos prêmios adicionados ao catálogo de recompensas!', 
      time: '6 dias atrás', 
      unread: false,
      type: 'message',
      icon: 'ri-gift-line',
      color: 'purple',
      category: 'mensagem'
    }
  ]);

  // Combinar notificações do sistema com convites de grupo
  const allNotifications = [...notifications];
  
  // Adicionar convites de grupo como notificações
  convitesPendentes.forEach((convite) => {
    allNotifications.unshift({
      id: `convite-${convite.id}` as any,
      message: `🤝 Convite de grupo: ${convite.deEmpresaNome} convidou sua empresa para "${convite.grupoNome}"`,
      time: new Date(convite.dataEnvio).toLocaleDateString('pt-BR'),
      unread: true,
      type: 'group_invite' as any,
      icon: 'ri-group-line',
      color: 'purple',
      category: 'grupo',
      _conviteData: convite,
    } as any);
  });

  const unreadCount = notifications.filter(n => n.unread).length + (convitesPendentes.length > 0 ? convitesPendentes.length : 0);

  const handleDeleteNotification = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const handleMarkAsRead = (id: number) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, unread: false } : n
    ));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, unread: false })));
  };

  const getNotificationColor = (color: string) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      orange: 'bg-orange-100 text-orange-600',
      emerald: 'bg-emerald-100 text-emerald-600',
      slate: 'bg-slate-100 text-slate-600',
      purple: 'bg-purple-100 text-purple-600',
      red: 'bg-red-100 text-red-600'
    };
    return colors[color as keyof typeof colors] || 'bg-gray-100 text-gray-600';
  };

  // Cálculo do total usando os saldos dinâmicos
  const totalGeral = saldos.valorContaDigital + saldos.valorPontos;
  const totalGeralFormatado = `R$ ${totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSelecionarEmpresa = (empresa: typeof empresas[0]) => {
    selecionarEmpresa(empresa);
    setShowEmpresaSelector(false);
    setShowProfile(false);
  };

  const usuarioInfo = {
    nome: isAuthenticated ? (session.nome || 'Usuário') : 'Usuário',
    email: isAuthenticated ? (session.email || '') : '',
    cargo: isAuthenticated ? (session.role === 'admin_empresa' ? 'Administrador' : session.role === 'gerente' ? 'Gerente' : session.role === 'operador' ? 'Operador' : 'Visualizador') : '',
    empresa: empresaNome || empresaSelecionada.nome,
  };

  // Dados da empresa e usuário
  const empresaInfo = {
    nome: usuarioInfo.empresa,
    cnpj: empresaSelecionada.cnpj
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <Link to="/admin" className="flex items-center">
          <img
            src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/bc60bceae91e18084986991f9c64254f.png"
            alt="iBanX"
            className="h-8 md:h-11 w-auto"
          />
        </Link>

        {/* Header Controls */}
        <div className="flex items-center space-x-6">
          {/* Combo de Saldos */}
          <div className="relative">
            <button
              onClick={() => setShowSaldoCombo(!showSaldoCombo)}
              className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-wallet-3-line text-green-600"></i>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">{t('header.total')}:</span>
                <span className="font-semibold text-green-600 ml-1">
                  {hideSaldoValue ? '••••••' : totalGeralFormatado}
                </span>
              </div>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-gray-400"></i>
              </div>
            </button>

            {showSaldoCombo && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">{t('header.balanceSummary')}</h3>
                  <button
                    onClick={() => setHideSaldoValue(!hideSaldoValue)}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                    title={hideSaldoValue ? t('header.showValues') : t('header.hideValues')}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={hideSaldoValue ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                    </div>
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  {/* Conta Digital */}
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-bank-card-line text-white"></i>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{t('header.digitalAccount')}</p>
                        <p className="text-sm text-gray-600">{t('header.availableBalance')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{hideSaldoValue ? '••••••' : saldos.contaDigital}</p>
                      <button
                        onClick={() => {
                          setShowSaldoCombo(false);
                          window.REACT_APP_NAVIGATE('/admin/incentivo/extrato-conta-digital');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap"
                      >
                        {t('header.viewStatement')}
                      </button>
                    </div>
                  </div>

                  {/* Pontos de Incentivo */}
                  <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-coin-line text-white"></i>
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{t('header.incentivePoints')}</p>
                        <p className="text-sm text-gray-600">{t('header.totalAccumulated')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{hideSaldoValue ? '••••••' : `${saldos.pontosIncentivo} pts`}</p>
                      <button
                        onClick={() => {
                          setShowSaldoCombo(false);
                          if (window.REACT_APP_NAVIGATE) {
                            window.REACT_APP_NAVIGATE('/admin/incentivo/extrato-pontos');
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap"
                      >
                        {t('header.viewStatement')}
                      </button>
                    </div>
                  </div>

                  {/* Resumo Total */}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{t('header.totalEquity')}</p>
                        <p className="text-xs text-gray-500">{t('header.accountPlusPoints')}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-blue-600">
                          {hideSaldoValue ? '••••••' : totalGeralFormatado}
                        </p>
                        <p className="text-xs text-gray-500">
                          {hideSaldoValue ? '••••••' : `${saldos.contaDigital} + ${saldos.pontosIncentivo} pts`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Seletor de Idioma */}
          <LanguageSelector />

          {/* Notificações */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-notification-3-line text-xl"></i>
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('header.notifications')}</h3>
                    <p className="text-xs text-gray-500 mt-1">Campanha: {empresaSelecionada.nome}</p>
                  </div>
                  {unreadCount > 0 && (
                    <button 
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap"
                    >
                      {t('header.markAllAsRead')}
                    </button>
                  )}
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {allNotifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className="ri-notification-off-line text-gray-400 text-2xl"></i>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">{t('header.noNotifications')}</p>
                    </div>
                  ) : (
                    allNotifications.map((notification: any) => (
                      <div
                        key={notification.id}
                        onClick={() => {
                          if (notification.type !== 'group_invite') {
                            handleMarkAsRead(notification.id);
                          }
                        }}
                        className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                          notification.unread ? 'bg-blue-50/50' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-10 h-10 rounded-lg ${getNotificationColor(notification.color)} flex items-center justify-center flex-shrink-0`}>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={notification.icon}></i>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className={`text-sm ${notification.unread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                  {notification.message}
                                </p>
                                {notification.type === 'group_invite' && notification._conviteData ? (
                                  <div className="flex items-center space-x-2 mt-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        aceitarConvite(notification._conviteData.id);
                                      }}
                                      className="px-3 py-1 text-xs font-bold text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg cursor-pointer whitespace-nowrap"
                                    >
                                      Aceitar
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        rejeitarConvite(notification._conviteData.id);
                                      }}
                                      className="px-3 py-1 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg cursor-pointer whitespace-nowrap"
                                    >
                                      Rejeitar
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="text-xs text-gray-500">{notification.time}</span>
                                    <span className="text-xs text-gray-400">&bull;</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      notification.category === 'premiacao' ? 'bg-purple-100 text-purple-700' :
                                      notification.category === 'missao' ? 'bg-emerald-100 text-emerald-700' :
                                      notification.category === 'alerta' ? 'bg-orange-100 text-orange-700' :
                                      notification.category === 'grupo' ? 'bg-purple-100 text-purple-700' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      {notification.category === 'premiacao' ? '🎁 Premiação' :
                                       notification.category === 'missao' ? '✅ Missão' :
                                       notification.category === 'alerta' ? '⚠️ Alerta' :
                                       notification.category === 'grupo' ? '🤝 Grupo' :
                                       '📢 Mensagem'}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {notification.type !== 'group_invite' ? (
                                <button
                                  onClick={(e) => handleDeleteNotification(notification.id, e)}
                                  className="text-gray-400 hover:text-red-600 ml-2 cursor-pointer"
                                  title={t('header.deleteNotification')}
                                >
                                  <div className="w-4 h-4 flex items-center justify-center">
                                    <i className="ri-close-line"></i>
                                  </div>
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    rejeitarConvite(notification._conviteData?.id);
                                  }}
                                  className="text-gray-400 hover:text-red-600 ml-2 cursor-pointer"
                                  title="Rejeitar convite"
                                >
                                  <div className="w-4 h-4 flex items-center justify-center">
                                    <i className="ri-close-line"></i>
                                  </div>
                                </button>
                              )}
                            </div>
                            {notification.unread && notification.type !== 'group_invite' && (
                              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {allNotifications.length > 0 && (
                  <div className="p-3 text-center border-t border-gray-200">
                    <button className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap">
                      {t('header.viewAllNotifications')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Perfil do Usuário */}
          <div className="relative">
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center space-x-3 p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20headshot%20with%20clean%20background%2C%20corporate%20style%2C%20friendly%20expression%2C%20high%20quality%20portrait&width=40&height=40&seq=user-avatar-1&orientation=squarish"
                alt="Perfil"
                className="w-8 h-8 rounded-full object-cover"
              />
              <div className="text-left">
                <p className="text-sm font-medium text-gray-900">{usuarioInfo.nome}</p>
                <p className="text-xs text-gray-500">{usuarioInfo.cargo}</p>
              </div>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-down-s-line text-gray-400"></i>
              </div>
            </button>

            {showProfile && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                {/* Informações da Empresa com Seletor */}
                <div className="p-4 bg-blue-50 border-b border-blue-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-building-line text-white"></i>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{empresaInfo.nome}</p>
                      <p className="text-xs text-gray-600">CNPJ: {empresaInfo.cnpj}</p>
                      {grupoAtual && (
                        <p className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full inline-block mt-1">
                          <i className="ri-group-line mr-1"></i>
                          {grupoAtual.nome}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setShowEmpresaSelector(!showEmpresaSelector)}
                      className="p-2 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                      title={t('header.switchCompany')}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-left-right-line text-blue-600"></i>
                      </div>
                    </button>
                  </div>

                  {/* Seletor de Empresas */}
                  {showEmpresaSelector && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">{t('header.selectCompany')}</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {empresas.map((empresa) => (
                          <button
                            key={empresa.id}
                            onClick={() => handleSelecionarEmpresa(empresa)}
                            className={`w-full flex items-center space-x-3 p-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                              empresa.id === empresaSelecionada.id
                                ? 'bg-blue-200 border-2 border-blue-400'
                                : 'bg-white hover:bg-blue-100 border border-blue-200'
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              empresa.id === empresaSelecionada.id ? 'bg-blue-500' : 'bg-blue-400'
                            }`}>
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-building-line text-white text-sm"></i>
                              </div>
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className={`text-xs font-semibold truncate ${
                                empresa.id === empresaSelecionada.id ? 'text-blue-900' : 'text-gray-900'
                              }`}>
                                {empresa.nome}
                              </p>
                              <p className="text-xs text-gray-600">CNPJ: {empresa.cnpj}</p>
                            </div>
                            {empresa.id === empresaSelecionada.id && (
                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <i className="ri-check-line text-blue-600 text-lg"></i>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Informações do Usuário */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <img
                      src="https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20headshot%20with%20clean%20background%2C%20corporate%20style%2C%20friendly%20expression%2C%20high%20quality%20portrait&width=48&height=48&seq=user-avatar-2&orientation=squarish"
                      alt="Perfil"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{usuarioInfo.nome}</p>
                      <p className="text-sm text-gray-500">{usuarioInfo.email}</p>
                      <p className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full inline-block mt-1">
                        {usuarioInfo.cargo}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Menu de Opções */}
                <div className="py-2">
                  <Link to="/perfil" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center mr-3">
                      <i className="ri-user-line"></i>
                    </div>
                    {t('header.myProfile')}
                  </Link>
                  <Link to="/empresa" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center mr-3">
                      <i className="ri-building-line"></i>
                    </div>
                    {t('header.companyData')}
                  </Link>
                  <Link to="/configuracoes" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center mr-3">
                      <i className="ri-settings-line"></i>
                    </div>
                    {t('header.settings')}
                  </Link>
                  <Link to="/admin/gestao-acesso" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center mr-3">
                      <i className="ri-shield-user-line"></i>
                    </div>
                    Gestão de Acesso
                  </Link>
                  <div className="border-t border-gray-200 mt-2 pt-2">
                    <button
                      onClick={() => {
                        logout();
                        setShowProfile(false);
                        window.location.href = '/admin/login';
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-4 h-4 flex items-center justify-center mr-3">
                        <i className="ri-logout-box-line"></i>
                      </div>
                      {t('header.logout')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}